import React, { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { motion, useReducedMotion, useScroll, useSpring, useTransform } from 'framer-motion';
import { Navbar } from './Navbar';
import { FooterSection } from '../../Pages/Home/components/FooterSection';
import { WhatsAppBubble } from '../common/WhatsAppBubble';

export const RootLayout: React.FC = () => {
  const { pathname } = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const vectorY = useSpring(useTransform(scrollYProgress, [0, 1], [0, -280]), {
    stiffness: 45,
    damping: 22,
  });
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  // Scroll to top on route navigation
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [pathname]);

  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;

    const revealTargets = new Set<Element>();
    const reveal = (target: Element) => {
      if (target.matches('[data-scroll-reveal]')) {
        revealTargets.add(target);
        observer.observe(target);
      }
      target.querySelectorAll?.('[data-scroll-reveal]').forEach((element) => {
        revealTargets.add(element);
        observer.observe(element);
      });
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });

    if (reduceMotion) {
      main.querySelectorAll('[data-scroll-reveal]').forEach((element) => element.classList.add('is-visible'));
    } else {
      reveal(main);
      const mutations = new MutationObserver((records) => {
        records.forEach((record) => record.addedNodes.forEach((node) => {
          if (node instanceof Element) reveal(node);
        }));
      });
      mutations.observe(main, { childList: true, subtree: true });
      return () => {
        observer.disconnect();
        mutations.disconnect();
        revealTargets.clear();
      };
    }

    return () => observer.disconnect();
  }, [pathname, reduceMotion]);

  return (
    <div className="relative min-h-screen bg-[#0b0b0c] text-[#f4f0e9] flex flex-col selection:bg-[#f15c12]/25 selection:text-[#fffaf2]">
      {/* Scroll Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-[2px] bg-[#f15c12] z-50 origin-left"
        style={{ scaleX }}
      />

      {/* Subtle Editorial Noise Texture */}
      <div className="noise-overlay" aria-hidden="true" />

      <motion.div
        aria-hidden="true"
        className="pointer-events-none fixed inset-y-0 right-0 z-0 hidden w-[28vw] overflow-hidden opacity-[0.13] lg:block"
        style={{ y: reduceMotion ? 0 : vectorY }}
      >
        <svg viewBox="0 0 320 900" fill="none" className="h-full w-full" preserveAspectRatio="xMidYMid slice">
          <path d="M320 36H192C126 36 72 90 72 156v156c0 66 54 120 120 120h128M320 588H220c-66 0-120 54-120 120v192" stroke="url(#wire-gradient)" strokeWidth="1" />
          <circle cx="192" cy="156" r="7" fill="#f15c12" />
          <circle cx="192" cy="432" r="7" fill="#f15c12" />
          <circle cx="220" cy="588" r="7" fill="#f15c12" />
          <defs>
            <linearGradient id="wire-gradient" x1="84" y1="34" x2="283" y2="781" gradientUnits="userSpaceOnUse">
              <stop stopColor="#f15c12" />
              <stop offset="1" stopColor="#f4f0e9" stopOpacity="0.2" />
            </linearGradient>
          </defs>
        </svg>
      </motion.div>

      {/* Navigation */}
      <Navbar />

      {/* Main Content Area */}
      <main ref={mainRef} className="relative z-10 flex-grow pt-24">
        <Outlet />
      </main>

      {/* Global Footer */}
      <FooterSection />

      {/* Global WhatsApp contact */}
      <WhatsAppBubble />
    </div>
  );
};
